import React from 'react'
import { Button, Tooltip } from 'antd'
import { useIntl } from 'react-intl'

import dtnaLogo from '../../assets/images/customer-logos/dtna.svg'
import slateLogo from '../../assets/images/customer-logos/slate.png'
import stellantisLogo from '../../assets/images/customer-logos/stellantis.svg'
import tweddleLogo from '../../assets/images/customer-logos/tweddle.svg'
import zooxLogo from '../../assets/images/customer-logos/zoox.png'

/**
 * Bundled logos keyed by lower-cased Cognito provider name. Logos are never
 * taken from configuration or a URL, so the page cannot be made to load
 * arbitrary images. A provider without an entry here is shown by its name, so
 * an entry for a provider not yet attached to any pool (slate, dtna) is simply
 * unused until it is.
 *
 * Every file is dark artwork on a transparent background, trimmed to its
 * content, because provider buttons are white in light and dark themes alike.
 *
 * `height` (px) is tuned per logo so they read as the same size: wordmarks
 * differ in shape and weight, so a compact or thin one is drawn taller and a
 * wide or heavy one shorter. A new logo needs a height chosen by eye against
 * the others.
 */
const PROVIDER_LOGOS = {
  dtna: { src: dtnaLogo, height: 23 },
  slate: { src: slateLogo, height: 16 },
  stellantis: { src: stellantisLogo, height: 26 },
  tweddle: { src: tweddleLogo, height: 27 },
  zoox: { src: zooxLogo, height: 21 },
}

/**
 * Starts federated sign-in through one identity provider. The button shows the
 * provider's logo, or its name when there is no logo; the full "Sign in with"
 * wording is carried by the tooltip and the accessible name.
 */
export default function ProviderButton({ provider, busy, onSelect }) {
  const intl = useIntl()
  const logo = PROVIDER_LOGOS[provider.name.toLowerCase()]
  const action = intl.formatMessage(
    { id: 'auth.signInWith' },
    { provider: provider.label },
  )

  return (
    <Tooltip title={action}>
      <Button
        block
        size="large"
        className="login-provider-button"
        aria-label={action}
        data-testid={`login-provider-${provider.name}`}
        disabled={busy}
        onClick={() => onSelect(provider.name)}
      >
        {logo ? (
          <img
            src={logo.src}
            alt=""
            data-testid="login-provider-logo"
            style={{ height: logo.height }}
            className="login-provider-logo"
          />
        ) : (
          provider.label
        )}
      </Button>
    </Tooltip>
  )
}
